import { useEffect, useState } from "react";
import { Loader2, Shuffle, Sparkles, BookOpen, Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { http } from "@/lib/api";
import { Stars } from "@/components/CaseRating";

const CaseRow = ({ c, score, onPick, disabled }) => (
  <button
    data-testid={`case-row-${c.id}`}
    onClick={() => onPick(c.id)}
    disabled={disabled}
    className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg hover:bg-[var(--muted-bg)] transition-colors disabled:opacity-50"
  >
    {c.source === "ai" ? <Sparkles size={15} style={{ color: "var(--accent)" }} className="shrink-0" /> : <BookOpen size={15} style={{ color: "var(--ink-3)" }} className="shrink-0" />}
    <span className="flex-1 text-sm">
      {c.source === "bank" && <span className="font-mono text-xs mr-2" style={{ color: "var(--ink-3)" }}>#{c.number}</span>}
      {c.title}
      {c.collection === "ANZCA sample" && <span data-testid={`case-row-college-tag-${c.id}`} className="ml-2 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "color-mix(in srgb, var(--ok) 14%, transparent)", color: "var(--ok)" }}>ANZCA</span>}
      {c.collection === "Crisis manual" && <span data-testid={`case-row-crisis-tag-${c.id}`} className="ml-2 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "color-mix(in srgb, var(--alert) 14%, transparent)", color: "var(--alert)" }}>Crisis</span>}
      {c.collection === "Past exam recall" && <span data-testid={`case-row-recall-tag-${c.id}`} className="ml-2 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "var(--muted-bg)", color: "var(--accent)" }}>Past recall</span>}
    </span>
    {c.rating_count > 0 && <span data-testid={`case-row-rating-${c.id}`} title={`${c.rating_count} rating(s)`}><Stars value={c.avg_rating} /></span>}
    {score != null && <span data-testid={`case-row-score-${c.id}`} className="font-mono text-xs font-bold px-2 py-0.5 rounded" style={{ background: "var(--muted-bg)" }}>{score}%</span>}
    <Play size={14} className="shrink-0" style={{ color: "var(--accent)" }} />
  </button>
);

export const DomainCasesDialog = ({ domain, onClose, onPick, onRandom, onGenerate, lastScores, loading }) => {
  const [cases, setCases] = useState(null);
  useEffect(() => {
    if (!domain) return;
    setCases(null);
    http.get("/cases", { params: { domain: domain.number } }).then((r) => setCases(r.data));
  }, [domain]);

  return (
    <Dialog open={!!domain} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="domain-cases-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto" style={{ background: "var(--surface)" }}>
        <span className="font-mono text-xs font-bold" style={{ color: "var(--accent)" }}>D{String(domain?.number || 0).padStart(2, "0")}</span>
        <DialogTitle className="font-display text-xl -mt-2">{domain?.name}</DialogTitle>
        <DialogDescription style={{ color: "var(--ink-3)" }}>Pick a specific case, or let the trainer choose.</DialogDescription>
        <div className="flex flex-wrap gap-2">
          <button data-testid="domain-random-case-button" onClick={onRandom} disabled={!!loading} className="btn-primary h-10 px-4 rounded-full text-sm font-semibold flex items-center gap-2">
            <Shuffle size={15} /> Random bank case
          </button>
          <button data-testid="domain-generate-ai-case-button" onClick={onGenerate} disabled={!!loading} className="btn-ghost h-10 px-4 rounded-full text-sm font-medium flex items-center gap-2">
            <Sparkles size={15} /> Write a new AI case
          </button>
        </div>
        {loading && <p data-testid="domain-loading-indicator" className="text-sm flex items-center gap-2" style={{ color: "var(--ink-2)" }}><Loader2 size={15} className="animate-spin" /> {loading}</p>}
        <div data-testid="domain-case-list" className="mt-2 -mx-2">
          {!cases ? <Loader2 className="animate-spin mx-auto" /> : cases.map((c) => (
            <CaseRow key={c.id} c={c} score={lastScores[c.id]} onPick={onPick} disabled={!!loading} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
